import { Box, CircularProgress } from '@mui/material';
import { useState } from 'react';
import DropdownSelect from '../../components/DropdownSelect';
import ToastMessage from '../../components/ToastMessage';
import PendingFeeSummaryTable from './components/PendingFeeSummaryTable';
import { SnackbarSeverityType } from '../../enums';
import { academicYears } from '../../constants/academicYears';
import { useSchoolClassesConfigHook } from '../common/hooks/useSchoolClassesConfig';
import { RoundedButton, SearchInputWrapper } from './styled';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { Dayjs } from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import report from '../../apis/report';
import { transformPendingFeeSummaryData } from './utils/transformPendingFeeSummaryData';
import CustomDatePicker from '../../components/DatePicker';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { downloadReport } from '../../utils/reportUtils';
import { PaperWrapper, Title } from '../common/styled';

const PendingFeeSummaryPage = () => {
  const [selectedAcademicYear, setSelectedAcademicYear] =
    useState<string>('All');
  const [selectedGrade, setSelectedGrade] = useState<string>('All');
  const [selectedDivision, setSelectedDivision] = useState<string>('All');
  const [hasSearched, setHasSearched] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [pendingFeeReportData, setPendingFeeReportData] = useState<any>([]);

  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<SnackbarSeverityType>(
    SnackbarSeverityType.success
  );

  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());

  const { gradeOptions, divisionOptions } = useSchoolClassesConfigHook();

  const gradeDropdownOptions = [
    { value: 'All', label: 'All' },
    ...gradeOptions
  ];
  const divisionDropdownOptions = [
    { value: 'All', label: 'All' },
    ...divisionOptions
  ];

  const academicYearOptions = [
    { value: 'All', label: 'All' },
    ...academicYears
  ];

  const handleCloseToast = () => {
    setToastOpen(false);
  };

  const showToast = (message: string, severity: SnackbarSeverityType) => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  const feeReportParams = {
    date: selectedDate ? selectedDate.format('YYYY-MM-DD') : '',
    academicYear: selectedAcademicYear === 'All' ? '' : selectedAcademicYear,
    grade: selectedGrade === 'All' ? '' : selectedGrade,
    division: selectedDivision === 'All' ? '' : selectedDivision
  };

  const handleSearch = async () => {
    setIsSearching(true);
    setPendingFeeReportData([]);
    try {
      const response = await report.fetchPendingFeeSummaryReport(
        feeReportParams
      );
      const transformedPendingFeeReport = transformPendingFeeSummaryData(
        response as any
      );
      setPendingFeeReportData(transformedPendingFeeReport);
      showToast(
        'Successfully fetched pending fee summary report',
        SnackbarSeverityType.success
      );
    } catch (error: any) {
      showToast(
        error.errors?.[0].message ||
          'Error fetching pending fee summary report',
        SnackbarSeverityType.error
      );
    } finally {
      setIsSearching(false);
      setHasSearched(true);
    }
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const response = await report.fetchPendingFeeSummaryReport(
        feeReportParams
      );
      const transformedPendingFeeReport = transformPendingFeeSummaryData(
        response as any
      );

      if (isEmpty(transformedPendingFeeReport)) {
        showToast(
          'There is no data available to download as an Excel file.',
          SnackbarSeverityType.error
        );
        return;
      }

      const fileName = `Pending Fee Summary Report -${moment().format(
        'DD-MM-YYYY_HH-mm-ss'
      )}`;

      downloadReport(
        transformedPendingFeeReport,
        fileName,
        'Pending Fee Summary Report',
        showToast
      );

      showToast('Report downloaded successfully', SnackbarSeverityType.success);
    } catch (error: any) {
      showToast(
        error.errors?.[0].message ||
          'Error downloading pending fee summary report',
        SnackbarSeverityType.error
      );
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Box>
      <Title>Pending Fee Summary Report</Title>
      <PaperWrapper
        sx={{
          minHeight: 'auto'
        }}
      >
        <SearchInputWrapper>
          <Box sx={{ flex: 1 }}>
            <DropdownSelect
              label='Academic Year'
              placeholder='Select Academic Year'
              name='academicYear'
              value={selectedAcademicYear}
              options={academicYearOptions}
              onChange={(event) => {
                setSelectedAcademicYear(event.target.value as string);
                setHasSearched(false);
              }}
              hasTopLabel
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <DropdownSelect
              label='Grade'
              placeholder='Select Grade'
              name='grade'
              value={selectedGrade}
              options={gradeDropdownOptions}
              onChange={(event) => {
                setSelectedGrade(event.target.value as string);
                setHasSearched(false);
              }}
              hasTopLabel
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <DropdownSelect
              label='Division'
              placeholder='Select Division'
              name='Division'
              value={selectedDivision}
              options={divisionDropdownOptions}
              onChange={(event) => {
                setSelectedDivision(event.target.value as string);
                setHasSearched(false);
              }}
              hasTopLabel
            />
          </Box>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ flex: 1, maxWidth: { sm: '280px' } }}>
              <CustomDatePicker
                label='From Date'
                onChange={(newValue: any) => {
                  setSelectedDate(newValue);
                  setHasSearched(false);
                }}
                value={selectedDate}
                width='100%'
              />
            </Box>
          </LocalizationProvider>
          <RoundedButton onClick={handleSearch} disabled={isSearching}>
            {isSearching ? (
              <CircularProgress size={24} color='inherit' />
            ) : (
              'View'
            )}
          </RoundedButton>
          <RoundedButton
            onClick={handleDownload}
            disabled={isDownloading || isSearching}
          >
            {isDownloading ? (
              <CircularProgress size={24} color='inherit' />
            ) : (
              'Download'
            )}
          </RoundedButton>
        </SearchInputWrapper>
      </PaperWrapper>
      {hasSearched && !isSearching && (
        <PendingFeeSummaryTable pendingFeeReportData={pendingFeeReportData} />
      )}
      <ToastMessage
        open={toastOpen}
        message={toastMessage}
        severity={toastSeverity}
        onClose={handleCloseToast}
      />
    </Box>
  );
};

export default PendingFeeSummaryPage;
